import type { FantasyBreakdownMember, FantasyContest, FantasyTeam } from '@brush/api-client';
import { Spinner } from '@brush/ui';
import { useState } from 'react';
import { budgetUnit, formatAmount, formatPoints, formatWhen } from '../../fantasy/roster';
import { usePlayer } from '../../player';
import { useFantasyTeamBreakdown } from '../../queries';
import { LeaderboardBreakdown } from '../LeaderboardBreakdown';

/**
 * §8.4's audit trail: what each of your picks contributed, and why.
 *
 * <b>Read from the frozen snapshot; nothing here is recomputed</b> — the same rule §7.4's
 * leaderboard breakdown follows, and for the same reason: recomputing would explain the total a
 * team *would* have beside the total it has, and the two part company exactly when the data has
 * moved, which is when somebody is most likely to be reading.
 *
 * <b>The zero-scoring member's sentence is the server's, rendered verbatim.</b> "Why did my guy
 * score nothing" is the most likely support question in this feature — §8.4 says so and puts the
 * answer in the payload precisely so that three clients do not word it three ways. A member who
 * never entered and a member who entered and busted are different facts with different sentences,
 * and neither is something this app should be composing.
 *
 * <b>Each member links into the leaderboard breakdown for that player</b> (§9.2), which is where
 * the points themselves are taken apart event by event. It expands in place rather than navigating:
 * this app does not route by URL, and the question is a follow-up to the row it is under.
 */
export function TeamBreakdown(props: {
  contest: FantasyContest;
  /** The caller's own entry, when the screen already holds it. */
  team?: FantasyTeam;
  /** Any team, from the standings. */
  teamId?: string;
  /** Rendered under a standings row rather than as a screen of its own. */
  inline?: boolean;
}) {
  const { contest, team, teamId, inline = false } = props;
  const { theme } = usePlayer();
  const query = useFantasyTeamBreakdown(teamId ?? team?.id ?? null);

  const frame = inline
    ? 'px-5 pb-4'
    : 'rounded-xl p-5';
  const style = inline
    ? { background: theme.panelLight }
    : { background: theme.panel, border: `1px solid ${theme.border}` };

  if (query.isLoading) {
    return (
      <div className={frame} style={style}>
        <Spinner label="Working out the points…" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <p role="alert" className={`${frame} text-sm`} style={style}>
        <span style={{ color: theme.textMuted }}>Couldn’t load this team’s breakdown just now.</span>
      </p>
    );
  }

  const breakdown = query.data;
  const unit = budgetUnit(contest);

  // Two absences, two sentences. A contest that has scored nothing and a team on a board that has
  // look identical if both just say "no points" — and only one is worth checking back on.
  if (breakdown.computedAt === null) {
    return (
      <p className={`${frame} text-sm`} style={style}>
        <span style={{ color: theme.textMuted }}>
          Nothing has been scored yet, so there is nothing to break down. Points appear once the
          room works out the first results.
        </span>
      </p>
    );
  }

  return (
    <div className={frame} style={style}>
      {!inline ? (
        <header className="mb-4">
          <h2 className="text-sm font-bold uppercase tracking-wider" style={{ color: theme.accent }}>
            {breakdown.teamName}
          </h2>
          <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
            Updated {formatWhen(breakdown.computedAt)}
          </p>
        </header>
      ) : null}

      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <span className="text-xl font-bold" style={{ color: theme.accent }}>
            {formatPoints(breakdown.totalPoints)}
          </span>
          <span className="ml-1.5 text-[10px] uppercase tracking-wider" style={{ color: theme.textMuted }}>
            points
          </span>
        </div>
        <span className="text-xs" style={{ color: theme.textMuted }}>
          {/* Their price at the draft, not today's: a pool can be re-priced for a later contest and
              what somebody agreed to pay is what `SalaryAtDraft` preserves (§5.1). */}
          Team cost {formatAmount(breakdown.salarySpent, unit)} of {formatAmount(breakdown.budget, unit)}
        </span>
      </div>

      <ul className="space-y-2">
        {breakdown.members.map((member) => (
          <MemberRow key={member.playerId} member={member} contest={contest} unit={unit} />
        ))}
      </ul>
    </div>
  );
}

function MemberRow({
  member,
  contest,
  unit,
}: {
  member: FantasyBreakdownMember;
  contest: FantasyContest;
  unit: string;
}) {
  const { theme } = usePlayer();
  const [open, setOpen] = useState(false);
  const isCaptain = member.role === 'captain';

  return (
    <li style={{ background: theme.panel, border: `1px solid ${theme.border}` }} className="overflow-hidden rounded-lg">
      <div className="flex items-start justify-between gap-3 p-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold">{member.playerName}</span>
            {isCaptain ? (
              <span
                className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                style={{ background: `${theme.accent}26`, color: theme.accent }}
              >
                Captain
              </span>
            ) : null}
          </div>

          <div className="mt-0.5 text-xs" style={{ color: theme.textMuted }}>
            {formatAmount(member.salaryAtDraft, unit)}
            {' · '}
            {member.eventsScored === 1 ? '1 event scored' : `${member.eventsScored} events scored`}
          </div>

          {/* §8.4's own sentence, verbatim. The server writes it where the facts are — a member who
              never entered and one who entered and cashed nothing are different answers, and this
              is the question the whole feature will be asked most. */}
          {member.explanation ? (
            <p className="mt-1.5 text-xs leading-relaxed" style={{ color: theme.text }}>
              {member.explanation}
            </p>
          ) : null}

          {/* The arithmetic, assembled from figures the server sent rather than multiplied here.
              A captain's multiplier is the one thing on this screen somebody will check by hand. */}
          {member.leaderboardPoints > 0 ? (
            <p className="mt-1 text-xs" style={{ color: theme.textMuted }}>
              {formatPoints(member.leaderboardPoints)} points
              {member.roleMultiplier !== 1 ? ` × ${member.roleMultiplier}` : ''}
              {' = '}
              {formatPoints(member.contribution)} for your team
            </p>
          ) : null}
        </div>

        <div className="shrink-0 text-right">
          <div className="font-bold" style={{ color: member.contribution > 0 ? theme.accent : theme.textMuted }}>
            {formatPoints(member.contribution)}
          </div>
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="mt-1 text-[10px] uppercase tracking-wider underline underline-offset-2 transition hover:opacity-80"
            style={{ color: theme.textMuted }}
          >
            {open ? 'Hide' : 'Why?'}
          </button>
        </div>
      </div>

      {/*
        §9.2: each member links into the leaderboard breakdown for that player. The contest names
        the board it scores from, so the existing component does the rest — the points a fantasy
        member contributed *are* that player's leaderboard points, and there is no second
        derivation to show.

        `breakdownUrl` on the payload is built server-side and names the same pair; it is not
        fetched by hand here, because the app already has a component and a cache for exactly this
        read and two paths to one resource would be two things to keep in step.
      */}
      {open ? (
        <div style={{ borderTop: `1px solid ${theme.border}` }}>
          {/* No `playersRanked`: the board's size is not on this screen, and "3rd of 274" against
              a number nobody can see is worse than "3rd". */}
          <LeaderboardBreakdown
            leaderboardId={contest.leaderboardId}
            playerId={member.playerId}
          />
        </div>
      ) : null}
    </li>
  );
}
